import React, { useEffect, useRef, useState } from 'react';
import confetti from 'canvas-confetti';
import { Sparkles, PartyPopper, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const STORAGE_KEY = 'ewen_welcome_seen_v1';
const GOLD = '#c9a56a';
const DEEP = '#0f1a14';
const CREAM = '#f6f1e7';

function fireCannon(sideRatio, angle) {
  confetti({
    particleCount: 90,
    angle,
    spread: 70,
    origin: { x: sideRatio, y: 1 },
    startVelocity: 60,
    ticks: 220,
    scalar: 1.1,
    colors: [GOLD, '#e8caa1', CREAM, '#ffffff', '#8a6b3a'],
  });
}

function fireTop(x) {
  confetti({
    particleCount: 40,
    angle: 270,
    spread: 60,
    origin: { x, y: -0.05 },
    startVelocity: 25,
    ticks: 260,
    gravity: 0.8,
    scalar: 0.9,
    colors: [GOLD, CREAM, '#e8caa1', '#ffffff'],
  });
}

function fireBurst() {
  const defaults = { startVelocity: 30, spread: 360, ticks: 90, zIndex: 100, scalar: 0.9 };
  const particleCount = 50;
  confetti({
    ...defaults,
    particleCount,
    origin: { x: 0.5, y: 0.5 },
    colors: [GOLD, CREAM, '#e8caa1', '#ffffff'],
  });
}

export default function WelcomeOverlay() {
  const [open, setOpen] = useState(false);
  const [entering, setEntering] = useState(true);
  const timers = useRef([]);

  useEffect(() => {
    const seen = typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY);
    if (seen) return;
    setOpen(true);
    document.body.style.overflow = 'hidden';

    const push = (fn, ms) => timers.current.push(setTimeout(fn, ms));

    // Cannons volley
    push(() => fireCannon(0.05, 60), 250);
    push(() => fireCannon(0.95, 120), 400);
    push(() => fireCannon(0.1, 55), 900);
    push(() => fireCannon(0.9, 125), 1050);
    push(() => fireBurst(), 1500);
    push(() => fireTop(0.2), 1800);
    push(() => fireTop(0.5), 2000);
    push(() => fireTop(0.8), 2200);
    push(() => fireCannon(0.2, 70), 3200);
    push(() => fireCannon(0.8, 110), 3350);
    push(() => setEntering(false), 800);

    return () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
      document.body.style.overflow = '';
    };
  }, []);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    // farewell burst
    fireBurst();
    setTimeout(() => setOpen(false), 350);
    document.body.style.overflow = '';
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] overflow-hidden"
      style={{ backgroundColor: DEEP }}
    >
      {/* Radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 40%, rgba(201,165,106,0.28) 0%, rgba(15,26,20,0) 55%)',
        }}
      />

      {/* Soft grain texture via SVG */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Floating twinkles */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 22 }).map((_, i) => (
          <span
            key={i}
            className="absolute block rounded-full"
            style={{
              width: 4 + (i % 3) * 2,
              height: 4 + (i % 3) * 2,
              left: `${(i * 47) % 100}%`,
              top: `${(i * 31) % 100}%`,
              backgroundColor: i % 3 === 0 ? GOLD : '#fff',
              opacity: 0.6,
              animation: `twinkle ${3 + (i % 4)}s ease-in-out ${(i % 10) * 0.2}s infinite alternate`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div
        className={`relative h-full w-full flex flex-col items-center justify-center px-6 text-center transition-all duration-700 ease-out ${
          entering ? 'opacity-0 translate-y-6' : 'opacity-100 translate-y-0'
        }`}
      >
        <div className="flex items-center gap-3 mb-6">
          <PartyPopper className="h-5 w-5" style={{ color: GOLD }} />
          <span
            className="text-xs uppercase tracking-[0.4em]"
            style={{ color: GOLD }}
          >
            SAME TRUST. NEW EXPERIENCE
          </span>
          <PartyPopper className="h-5 w-5 -scale-x-100" style={{ color: GOLD }} />
        </div>

        <div className="flex items-center justify-center mb-2">
          <div className="h-16 w-16 rounded-full grid place-items-center" style={{ backgroundColor: 'white' }}>
            <img src="/logo.svg" alt="" />
            </div>
        </div>

        <h1
          className="font-serif text-5xl md:text-7xl lg:text-8xl text-white leading-[1.02] max-w-4xl mt-6"
          style={{ animation: 'fadeUp 900ms ease-out 200ms both' }}
        >
          Welcome to the new
          <br />
          <em className="not-italic" style={{ color: GOLD }}>Ewen Realtors.</em>
        </h1>

        <p
          className="text-white/70 mt-6 max-w-xl text-lg leading-relaxed"
          style={{ animation: 'fadeUp 900ms ease-out 500ms both' }}
        >
          A brand-new home for our stories, our townships and your dreams. Cheers to the mountains — and to what you will build under them.
        </p>

        <div
          className="flex flex-wrap items-center justify-center gap-3 mt-10"
          style={{ animation: 'fadeUp 900ms ease-out 800ms both' }}
        >
          <Button
            onClick={close}
            className="rounded-none px-10 py-6 tracking-widest text-sm"
            style={{ backgroundColor: GOLD, color: DEEP }}
          >
            Step Inside <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          {/* <button
            onClick={close}
            className="text-xs uppercase tracking-widest text-white/50 hover:text-white transition-colors underline-offset-4 underline"
          >
            Skip Intro
          </button> */}
        </div>

        {/* Ribbon strip */}
        <div
          className="mt-14 flex items-center gap-6 text-white/60 text-xs uppercase tracking-[0.3em]"
          style={{ animation: 'fadeUp 900ms ease-out 1100ms both' }}
        >
          {/* <span className="flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" style={{ color: GOLD }} /> Since 2011</span> */}
          {/* <span className="h-px w-6 bg-white/20" /> */}
          <span>RERA UKREA05230000398</span>
          {/* <span className="h-px w-6 bg-white/20" />
          <span className="hidden sm:inline">Kathgodam, Uttarakhand</span> */}
        </div>
      </div>

      <style>{`
        @keyframes twinkle { from { transform: scale(0.6); opacity: 0.15; } to { transform: scale(1.1); opacity: 0.85; } }
      `}</style>
    </div>
  );
}
